import axios from 'axios';
import API from '../../config/API';
import { ONBOARDING_USE_API } from './flags';

/**
 * AI onboarding / import API (ONBOARDING-IMPORT-PLAN.md §7).
 *
 * One interface, two adapters:
 *   realApi  — the v2 endpoints the backend is building.
 *   mockApi  — plays the prototype's script on timers, persisted in
 *              sessionStorage so a reload behaves like a server would.
 *
 * Every method resolves to plain data (not an axios response) so callers
 * never branch on the adapter. Shapes are exactly §7.1–7.3.
 */

/* ───────────────────────── real adapter ───────────────────────── */

function authHeaders(extra = {}) {
  return {
    'accessToken': localStorage.getItem('accessToken') || '',
    ...extra,
  };
}

function unwrap(res) {
  const d = res?.data;
  if (d && d.statusCode === 0) {
    const err = new Error(d.error?.responseMessage || d.messages || 'Request failed');
    err.response = res;
    throw err;
  }
  return d?.responseData ?? d;
}

const realApi = {
  uploadFile(file, hint) {
    const fd = new FormData();
    fd.append('file', file);
    if (hint) fd.append('hint', hint);
    return axios.post(API.IMPORT_FILES, fd, { headers: authHeaders() }).then(unwrap);
  },
  createJob(fileIds) {
    return axios.post(API.IMPORT_JOBS, { fileIds }, { headers: authHeaders({ 'Content-Type': 'application/json' }) }).then(unwrap);
  },
  addFilesToJob(jobId, fileIds) {
    return axios.post(`${API.IMPORT_JOBS}/${jobId}/files`, { fileIds }, { headers: authHeaders({ 'Content-Type': 'application/json' }) }).then(unwrap);
  },
  getJob(jobId) {
    return axios.get(`${API.IMPORT_JOBS}/${jobId}`, { headers: authHeaders() }).then(unwrap);
  },
  listJobs() {
    return axios.get(API.IMPORT_JOBS, { headers: authHeaders() }).then(unwrap);
  },
  answer(jobId, itemId, answer) {
    return axios.post(`${API.IMPORT_JOBS}/${jobId}/answers`, { itemId, answer }, { headers: authHeaders({ 'Content-Type': 'application/json' }) }).then(unwrap);
  },
  mergeExact(jobId) {
    return axios.post(`${API.IMPORT_JOBS}/${jobId}/merge-exact`, {}, { headers: authHeaders({ 'Content-Type': 'application/json' }) }).then(unwrap);
  },
  commit(jobId) {
    return axios.post(`${API.IMPORT_JOBS}/${jobId}/commit`, {}, { headers: authHeaders({ 'Content-Type': 'application/json' }) }).then(unwrap);
  },
  rollback(jobId) {
    return axios.post(`${API.IMPORT_JOBS}/${jobId}/rollback`, {}, { headers: authHeaders({ 'Content-Type': 'application/json' }) }).then(unwrap);
  },
  getOnboardingState() {
    return axios.get(API.ONBOARDING_STATE, { headers: authHeaders() }).then(unwrap);
  },
  putOnboardingState(patch) {
    return axios.put(API.ONBOARDING_STATE, patch, { headers: authHeaders({ 'Content-Type': 'application/json' }) }).then(unwrap);
  },
};

/* ───────────────────────── mock adapter ───────────────────────── */

// The prototype's three files. Any dropped file is classified by extension /
// name so a reviewer can drag anything and still see the right story.
const MOCK_FILE_SCRIPT = [
  { match: /client|customer/i, source: 'fresha', tag: 'Fresha · 1,240', kinds: ['clients'], counts: { clients: 1240 }, trace: ['Recognised a Fresha export', '38 exact duplicate clients found'] },
  { match: /price|service|menu|\.(jpe?g|png|heic|pdf)$/i, source: 'photo', tag: '48 services', kinds: ['services', 'products'], counts: { services: 48, products: 22 }, trace: ['Read 48 services and 22 products from a photo'] },
  { match: /appoint|booking|diary|calendar/i, source: 'fresha', tag: '6 staff · rotas', kinds: ['bookings', 'staff', 'rota', 'hours'], counts: { bookings: 3102, staff: 6 }, trace: ['Found 6 people in your files', 'Read their hours and your opening hours'] },
];
const MOCK_FALLBACK = [
  { source: 'generic', kinds: ['clients'], counts: { clients: 312 }, trace: ['Read 312 clients from a spreadsheet'] },
  { source: 'generic', kinds: ['services'], counts: { services: 19 }, trace: ['Read 19 services with prices'] },
  { source: 'generic', kinds: ['bookings'], counts: { bookings: 640 }, trace: ['Found 640 bookings'] },
];

const MOCK_TAG = {
  fresha: 'Fresha', treatwell: 'Treatwell', phorest: 'Phorest', booksy: 'Booksy', photo: 'Photo', pdf: 'PDF', generic: 'Spreadsheet',
};

function mockQueue() {
  return [
    { id: 'q1', type: 'duplicate_pair', reason: 'phone',
      a: { clientId: 'mock-c-88', name: 'Sarah Jenkins', phone: '07700 900412', email: 'sarah.j88@gmail.com', visits: 14, sourceFile: 'clients_export.csv', sourceRow: 88 },
      b: { clientId: 'mock-c-1904', name: 'Sarah Jenkins-Moore', phone: '07700 900412', email: '', visits: 3, sourceFile: 'appointments_2026.csv', sourceRow: 1904 } },
    { id: 'q2', type: 'duplicate_pair', reason: 'email',
      a: { clientId: 'mock-c-214', name: 'Amara Okafor', phone: '07700 483920', email: 'amara.o@gmail.com', visits: 9, sourceFile: 'clients_export.csv', sourceRow: 214 },
      b: { clientId: 'mock-c-655', name: 'A. Okafor', phone: '', email: 'amara.o@gmail.com', visits: 2, sourceFile: 'appointments_2026.csv', sourceRow: 655 } },
    { id: 'q3', type: 'unmapped_service', service: { tempId: 's-31', name: 'Balayage Half', price: 95, duration: 120 },
      candidates: [{ categoryId: 'cat-colour', name: 'Colour' }, { categoryId: 'cat-cut', name: 'Cut & Finish' }, { categoryId: 'cat-treat', name: 'Treatments' }] },
    { id: 'q4', type: 'unresolved_booking', booking: { clientName: 'Ruth Kelly', date: '2026-08-25', start: '13:00', end: '15:00' }, missing: 'staff',
      candidates: [{ id: 'st-1', name: 'Nadia' }, { id: 'st-2', name: 'Jess Carter' }, { id: 'st-3', name: 'Marcus Reid' }] },
    { id: 'q5', type: 'decision', key: 'card_fees', context: { selfEmployedCount: 1 } },
    { id: 'q6', type: 'decision', key: 'client_sharing', context: { selfEmployedCount: 1 } },
    { id: 'q7', type: 'decision', key: 'guest_booking', context: {} },
    { id: 'q8', type: 'decision', key: 'marketplace', context: {} },
    { id: 'q9', type: 'decision', key: 'no_show_payer', context: { selfEmployedCount: 1, hasPolicy: true } },
    { id: 'q10', type: 'decision', key: 'consent_carry', context: { column: 'Marketing opt-in', optedIn: 412, total: 1240 } },
    { id: 'q11', type: 'decision', key: 'opening_hours', context: { hours: [
      { dayName: 'Monday', sTime: '09:00', eTime: '18:00', isClose: false },
      { dayName: 'Tuesday', sTime: '09:00', eTime: '18:00', isClose: false },
      { dayName: 'Wednesday', sTime: '09:00', eTime: '18:00', isClose: false },
      { dayName: 'Thursday', sTime: '09:00', eTime: '20:00', isClose: false },
      { dayName: 'Friday', sTime: '09:00', eTime: '18:00', isClose: false },
      { dayName: 'Saturday', sTime: '08:30', eTime: '17:00', isClose: false },
      { dayName: 'Sunday', sTime: '', eTime: '', isClose: true },
    ] } },
    { id: 'q12', type: 'staff_review', staff: [
      { tempId: 'st-2', name: 'Jess Carter', email: 'jess.carter@gmail.com', bookingsFound: 412, daysPattern: 'Tue to Sat', employmentTypeId: 1 },
      { tempId: 'st-3', name: 'Marcus Reid', email: 'marcus@reidhair.co.uk', bookingsFound: 268, daysPattern: 'Self-employed in your old system', employmentTypeId: 2 },
    ] },
  ];
}

const MOCK_PREVIEW_DAY = {
  date: '2026-08-25',
  label: 'Tuesday 25 August',
  bookings: [
    { start: '09:00', end: '10:00', clientName: 'Sarah Jenkins', serviceName: 'Cut & Finish', staffName: 'Nadia', tone: 'teal' },
    { start: '10:30', end: '12:00', clientName: 'Amara Okafor', serviceName: 'Full Head Colour', staffName: 'Nadia', tone: 'lilac' },
    { start: '13:00', end: '15:00', clientName: 'Ruth Kelly', serviceName: 'Balayage', staffName: 'Jess', tone: 'teal' },
    { start: '15:15', end: '16:00', clientName: 'Nia Roberts', serviceName: 'Blow Dry', staffName: 'Jess', tone: 'lilac' },
  ],
};

const MOCK_KEY = 'beu_onboarding_mock_v1';

function mockLoad() {
  try { return JSON.parse(sessionStorage.getItem(MOCK_KEY)) || { jobs: {}, state: null, seq: 0 }; }
  catch (e) { return { jobs: {}, state: null, seq: 0 }; }
}
function mockSave(db) {
  try { sessionStorage.setItem(MOCK_KEY, JSON.stringify(db)); } catch (e) { /* storage unavailable */ }
}
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

function classify(name, index) {
  const hit = MOCK_FILE_SCRIPT.find((s) => s.match.test(name));
  const spec = hit || MOCK_FALLBACK[index % MOCK_FALLBACK.length];
  return { source: spec.source, tag: spec.tag || null, kinds: spec.kinds, counts: spec.counts, trace: spec.trace };
}

function recomputeSummary(job) {
  const totals = { clients: 0, services: 0, products: 0, staff: 0, bookings: 0 };
  job.files.forEach((f) => {
    if (f.status !== 'recognised' || !f.analysis) return;
    Object.keys(f.analysis.counts).forEach((k) => { totals[k] = (totals[k] || 0) + f.analysis.counts[k]; });
  });
  const kinds = new Set();
  job.files.forEach((f) => (f.analysis?.kinds || []).forEach((k) => kinds.add(k)));
  // Queue only exists once something recognisable is in. Items are filtered
  // to what the files actually contain so the count is honest.
  const queue = mockQueue().filter((item) => {
    if (item.type === 'duplicate_pair' || item.key === 'consent_carry') return kinds.has('clients');
    if (item.type === 'unmapped_service') return kinds.has('services');
    if (item.type === 'unresolved_booking' || item.key === 'opening_hours') return kinds.has('bookings');
    if (item.type === 'staff_review' || item.key === 'card_fees' || item.key === 'client_sharing' || item.key === 'no_show_payer') return kinds.has('staff');
    return true; // guest_booking, marketplace: always relevant
  });
  const answered = new Set(Object.keys(job.answers || {}));
  job.queue = queue;
  job.summary = {
    ...totals,
    exactDuplicates: kinds.has('clients') ? 38 : 0,
    questions: queue.filter((q) => !answered.has(q.id)).length,
  };
  job.previewDay = kinds.has('bookings') ? MOCK_PREVIEW_DAY : null;
}

const mockApi = {
  async uploadFile(file) {
    await wait(300 + Math.random() * 300);
    const db = mockLoad();
    db.seq += 1;
    const fileId = `mock-f-${db.seq}`;
    db.files = db.files || {};
    db.files[fileId] = { fileId, name: file.name, mime: file.type, size: file.size };
    mockSave(db);
    return db.files[fileId];
  },
  async createJob(fileIds) {
    const db = mockLoad();
    db.seq += 1;
    const jobId = `mock-job-${db.seq}`;
    const job = { jobId, status: 'extracting', createdAt: new Date().toISOString(), files: [], answers: {}, merges: [] };
    db.jobs[jobId] = job;
    mockSave(db);
    await mockApi.addFilesToJob(jobId, fileIds);
    return { jobId, status: 'extracting' };
  },
  async addFilesToJob(jobId, fileIds) {
    const db = mockLoad();
    const job = db.jobs[jobId];
    if (!job) throw new Error('Unknown job');
    fileIds.forEach((fid, i) => {
      const meta = (db.files || {})[fid] || { fileId: fid, name: 'file' };
      const index = job.files.length;
      job.files.push({ fileId: fid, name: meta.name, status: 'reading', analysis: null, readyAt: Date.now() + 1400 + i * 900, _index: index });
    });
    job.status = 'extracting';
    mockSave(db);
    return { status: 'extracting' };
  },
  async getJob(jobId) {
    await wait(120);
    const db = mockLoad();
    const job = db.jobs[jobId];
    if (!job) throw new Error('Unknown job');
    const now = Date.now();
    let changed = false;
    job.files.forEach((f) => {
      if (f.status === 'reading' && now >= f.readyAt) {
        f.status = 'recognised';
        f.analysis = classify(f.name, f._index || 0);
        changed = true;
      }
    });
    if (job.status === 'extracting' && job.files.length && job.files.every((f) => f.status !== 'reading')) {
      job.status = 'extracted';
      changed = true;
    }
    if (job.status === 'committing' && now >= (job.commitReadyAt || 0)) {
      job.status = 'committed';
      job.committedAt = new Date().toISOString();
      changed = true;
    }
    recomputeSummary(job);
    if (changed) mockSave(db);
    return JSON.parse(JSON.stringify(job));
  },
  async listJobs() {
    const db = mockLoad();
    return Object.values(db.jobs)
      .filter((j) => j.status !== 'extracting' || j.files.length)
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
      .map((j) => {
        recomputeSummary(j);
        const ageH = (Date.now() - new Date(j.committedAt || j.createdAt).getTime()) / 36e5;
        return { jobId: j.jobId, createdAt: j.createdAt, files: j.files.map((f) => f.name), summary: j.summary, status: j.status, canRollback: j.status === 'committed' && ageH < 72 };
      });
  },
  async answer(jobId, itemId, answer) {
    const db = mockLoad();
    const job = db.jobs[jobId];
    if (!job) throw new Error('Unknown job');
    job.answers[itemId] = answer;
    mockSave(db);
    return { ok: true };
  },
  async mergeExact(jobId) {
    await wait(900);
    const db = mockLoad();
    const job = db.jobs[jobId];
    job.merges.push({ mergeId: `mock-merge-${Date.now()}`, count: 38 });
    mockSave(db);
    return { merged: 38 };
  },
  async commit(jobId) {
    const db = mockLoad();
    const job = db.jobs[jobId];
    job.status = 'committing';
    job.commitReadyAt = Date.now() + 1800;
    mockSave(db);
    return { status: 'committing' };
  },
  async rollback(jobId) {
    await wait(700);
    const db = mockLoad();
    const job = db.jobs[jobId];
    job.status = 'rolled_back';
    mockSave(db);
    return { status: 'rolled_back', kept: 0 };
  },
  // Mock-only: MergeClientsView cannot hit /user-merge/preview with fake ids,
  // so the layer renders a stand-in merge view in mock mode and confirms here.
  async mockConfirmMerge(jobId, pair) {
    await wait(600);
    const db = mockLoad();
    const job = db.jobs[jobId];
    const mergeId = `mock-merge-${Date.now()}`;
    job.merges.push({ mergeId, pair });
    mockSave(db);
    return { mergeId };
  },
  async getOnboardingState() {
    const db = mockLoad();
    return db.state || { status: 'new', activeJobId: null, checklist: {} };
  },
  async putOnboardingState(patch) {
    const db = mockLoad();
    db.state = { ...(db.state || { status: 'new', checklist: {} }), ...patch, checklist: { ...((db.state || {}).checklist || {}), ...(patch.checklist || {}) }, updatedAt: new Date().toISOString() };
    mockSave(db);
    return db.state;
  },
};

export const onboardingApi = ONBOARDING_USE_API ? realApi : mockApi;
export const IS_MOCK_API = !ONBOARDING_USE_API;
export const sourceTag = (source) => MOCK_TAG[source] || 'Recognised';
